import React, { useState, useMemo, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const DataTable = ({ columns, data, idField, onEdit, onDelete, onCreate, createLabel, loading, emptyMessage, exportFileName, importColumns, hideToolbarRight, hideSearch, importRef, templateRef, exportRef, extraActions }) => {
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const fileInputRef = useRef(null);
  const [importData, setImportData] = useState(null);
  const [importProgress, setImportProgress] = useState(null); // { current, total, ok, fail, errors }
  const importErrorsRef = useRef([]);
  // Snapshot de las filas originales del Excel para asociar errores a su fila al exportar
  const importRowsRef = useRef([]);

  const filtered = useMemo(() => {
    if (!filter) return data;
    const q = filter.toLowerCase();
    return data.filter(row =>
      columns.some(col => {
        const val = col.render ? null : row[col.key];
        return val && String(val).toLowerCase().includes(q);
      })
    );
  }, [data, filter, columns]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice(page * pageSize, (page + 1) * pageSize);

  const handleFilter = (val) => { setFilter(val); setPage(0); };
  const handlePageSize = (val) => { setPageSize(Number(val)); setPage(0); };

  // Callback global para que los handlers reporten progreso.
  // errorMsg puede ser string (mensaje) o ya un objeto {message, row, rowIndex}.
  const updateProgress = useCallback((current, total, ok, fail, errorMsg) => {
    if (errorMsg) {
      const errorObj = typeof errorMsg === 'string'
        ? { rowIndex: current, message: errorMsg, row: importRowsRef.current[current - 1] || null }
        : { rowIndex: errorMsg.rowIndex ?? current, message: errorMsg.message, row: errorMsg.row || null };
      importErrorsRef.current = [...importErrorsRef.current, errorObj];
    }
    setImportProgress({ current, total, ok, fail, errors: importErrorsRef.current });
  }, []);

  // Exponer updateProgress via window para que los handlers lo usen
  React.useEffect(() => {
    window._dtImportProgress = updateProgress;
    return () => { window._dtImportProgress = null; };
  }, [updateProgress]);

  // Export
  const handleExport = () => {
    const exportRows = filtered.map(row => {
      const obj = {};
      columns.forEach(col => { obj[col.label] = row[col.key] ?? ''; });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Datos');
    XLSX.writeFile(wb, `${exportFileName || 'datos'}.xlsx`);
  };

  // Estilos reutilizables para plantilla
  const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0F1B2D' } };
  const headerFont = { bold: true, color: { argb: 'FFFFFF' }, size: 11, name: 'Calibri' };
  const headerAlignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  const headerBorder = {
    top: { style: 'thin', color: { argb: '3B82F6' } },
    bottom: { style: 'thin', color: { argb: '3B82F6' } },
    left: { style: 'thin', color: { argb: '3B82F6' } },
    right: { style: 'thin', color: { argb: '3B82F6' } },
  };
  const optionalFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '6B7280' } };

  // Descargar plantilla con ExcelJS (headers estilizados)
  const handleDownloadTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Siscorp ERP';
    const cols = importColumns || columns.map(c => ({ key: c.label, label: c.label }));

    // --- Hoja "Datos" ---
    const wsData = wb.addWorksheet('Datos', {
      properties: { tabColor: { argb: '3B82F6' } },
      views: [{ state: 'frozen', ySplit: 1 }],
    });

    wsData.columns = cols.map(c => ({
      header: c.required ? `${c.label} (*)` : c.label,
      key: c.key,
      width: Math.max((c.label || '').length + 6, 18),
    }));

    const headerRow = wsData.getRow(1);
    headerRow.height = 28;
    headerRow.eachCell((cell, colNumber) => {
      const col = cols[colNumber - 1];
      cell.fill = col && col.required ? headerFill : optionalFill;
      cell.font = headerFont;
      cell.alignment = headerAlignment;
      cell.border = headerBorder;
    });

    // Fila de ejemplo
    const hasExample = cols.some(c => c.example !== undefined && c.example !== '');
    if (hasExample) {
      const exampleData = {};
      cols.forEach(c => { exampleData[c.key] = c.example ?? ''; });
      const exRow = wsData.addRow(exampleData);
      exRow.eachCell(cell => {
        cell.font = { italic: true, color: { argb: '94A3B8' }, size: 10, name: 'Calibri' };
        cell.alignment = { vertical: 'middle' };
      });
    }

    wsData.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: cols.length } };

    // --- Hoja "Instrucciones" ---
    const wsInst = wb.addWorksheet('Instrucciones', {
      properties: { tabColor: { argb: '64748B' } },
    });

    wsInst.mergeCells('A1:D1');
    const titleCell = wsInst.getCell('A1');
    titleCell.value = `PLANTILLA DE IMPORTACION - ${(exportFileName || 'datos').toUpperCase()}`;
    titleCell.font = { bold: true, size: 14, color: { argb: 'FFFFFF' }, name: 'Calibri' };
    titleCell.fill = headerFill;
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    wsInst.getRow(1).height = 36;

    const instrucciones = [
      '',
      'INSTRUCCIONES:',
      '1. Complete los datos en la hoja "Datos". NO modifique los nombres de las columnas.',
      '2. Los campos con fondo AZUL OSCURO son OBLIGATORIOS (*). Los de fondo GRIS son opcionales.',
      '3. Si existe una fila de ejemplo (texto gris italica), reemplacela con sus datos reales.',
      '4. Puede agregar tantas filas como necesite a partir de la fila 2.',
      '5. Guarde el archivo y utilice el boton "Importar" en la aplicacion.',
      '',
    ];
    instrucciones.forEach((text, i) => {
      const row = wsInst.getRow(i + 2);
      row.getCell(1).value = text;
      if (text.startsWith('INSTRUCCIONES')) {
        row.getCell(1).font = { bold: true, size: 12, color: { argb: '3B82F6' }, name: 'Calibri' };
      } else {
        row.getCell(1).font = { size: 10, name: 'Calibri' };
      }
    });

    const tableStartRow = instrucciones.length + 2;
    const tableHeaders = ['Campo', 'Obligatorio', 'Descripcion', 'Ejemplo'];
    const thRow = wsInst.getRow(tableStartRow);
    tableHeaders.forEach((h, i) => {
      const cell = thRow.getCell(i + 1);
      cell.value = h;
      cell.fill = headerFill;
      cell.font = headerFont;
      cell.alignment = headerAlignment;
      cell.border = headerBorder;
    });
    thRow.height = 24;

    cols.forEach((c, idx) => {
      const row = wsInst.getRow(tableStartRow + 1 + idx);
      row.getCell(1).value = c.label;
      row.getCell(1).font = { bold: true, size: 10, name: 'Calibri' };
      const reqCell = row.getCell(2);
      reqCell.value = c.required ? 'SI' : 'No';
      reqCell.font = { bold: c.required, size: 10, color: { argb: c.required ? 'EF4444' : '94A3B8' }, name: 'Calibri' };
      reqCell.alignment = { horizontal: 'center' };
      row.getCell(3).value = c.description || '';
      row.getCell(3).font = { size: 10, name: 'Calibri' };
      row.getCell(4).value = c.example !== undefined ? String(c.example) : '';
      row.getCell(4).font = { italic: true, size: 10, color: { argb: '64748B' }, name: 'Calibri' };
      for (let ci = 1; ci <= 4; ci++) {
        row.getCell(ci).border = { bottom: { style: 'hair', color: { argb: '334155' } } };
      }
    });

    wsInst.getColumn(1).width = 25;
    wsInst.getColumn(2).width = 14;
    wsInst.getColumn(3).width = 50;
    wsInst.getColumn(4).width = 25;

    // --- Hoja "Ejemplo" ---
    if (hasExample) {
      const wsEj = wb.addWorksheet('Ejemplo', {
        properties: { tabColor: { argb: '22C55E' } },
      });
      wsEj.columns = cols.map(c => ({
        header: c.required ? `${c.label} (*)` : c.label,
        key: c.key,
        width: Math.max((c.label || '').length + 6, 18),
      }));
      const ejHeaderRow = wsEj.getRow(1);
      ejHeaderRow.height = 28;
      ejHeaderRow.eachCell((cell, colNumber) => {
        const col = cols[colNumber - 1];
        cell.fill = col && col.required ? headerFill : optionalFill;
        cell.font = headerFont;
        cell.alignment = headerAlignment;
        cell.border = headerBorder;
      });
      const exampleData = {};
      cols.forEach(c => { exampleData[c.key] = c.example ?? ''; });
      wsEj.addRow(exampleData);
      const noteRow = wsEj.addRow({});
      noteRow.getCell(1).value = '-- Estos son datos de ejemplo. Elimine esta hoja antes de importar --';
      noteRow.getCell(1).font = { italic: true, color: { argb: 'EF4444' }, size: 9, name: 'Calibri' };
    }

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `plantilla_${exportFileName || 'datos'}.xlsx`);
  };

  // Import
  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'binary' });
      const sheetName = wb.SheetNames.includes('Datos') ? 'Datos' : wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      let rows = XLSX.utils.sheet_to_json(ws);
      if (rows.length > 0) {
        const hasMarker = Object.keys(rows[0]).some(k => k.includes('(*)'));
        if (hasMarker) {
          rows = rows.map(row => {
            const clean = {};
            Object.entries(row).forEach(([k, v]) => {
              clean[k.replace(/\s*\(\*\)/, '').trim()] = v;
            });
            return clean;
          });
        }
      }
      setImportData(rows);
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const handleStartImport = () => {
    if (typeof window._dtImportHandler === 'function') {
      importErrorsRef.current = [];
      importRowsRef.current = importData || []; // snapshot para correlacionar errores con filas
      setImportProgress({ current: 0, total: importData.length, ok: 0, fail: 0, errors: [] });
      window._dtImportHandler(importData);
    }
    setImportData(null);
  };

  // Exporta el detalle de errores de la importación a un Excel.
  // Cada fila lleva: # Fila + columnas originales del Excel + Error.
  const handleExportImportErrors = () => {
    const errs = importProgress?.errors || [];
    if (errs.length === 0) return;
    // Determinar el conjunto de columnas a exportar.
    // Preferimos las claves de la primera fila válida; si no hay, usamos las definidas en importColumns.
    const firstRowWithData = errs.find(e => e.row && Object.keys(e.row).length > 0);
    const baseKeys = firstRowWithData
      ? Object.keys(firstRowWithData.row)
      : (importColumns || []).map(c => c.key);
    const exportRows = errs.map(e => {
      const out = { '# Fila': e.rowIndex ?? '' };
      baseKeys.forEach(k => { out[k] = e.row?.[k] ?? ''; });
      out['Error'] = e.message || '';
      return out;
    });
    const ws = XLSX.utils.json_to_sheet(exportRows);
    // Auto-ancho aproximado para que las columnas no salgan apretadas
    const colWidths = Object.keys(exportRows[0]).map(k => ({
      wch: Math.min(60, Math.max(k.length, ...exportRows.map(r => String(r[k] ?? '').length)) + 2),
    }));
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Errores');
    XLSX.writeFile(wb, `${exportFileName || 'datos'}_errores_importacion.xlsx`);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(0, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible);
    if (end - start < maxVisible) start = Math.max(0, end - maxVisible);
    for (let i = start; i < end; i++) pages.push(i);
    return pages;
  };

  // Exponer funciones via refs para control externo
  if (importRef) importRef.current = handleImportClick;
  if (templateRef) templateRef.current = handleDownloadTemplate;
  if (exportRef) exportRef.current = handleExport;

  if (loading && !importProgress) return <div className="empty-state">Cargando...</div>;

  const progressPct = importProgress ? Math.round((importProgress.current / importProgress.total) * 100) : 0;
  const progressDone = importProgress && importProgress.current >= importProgress.total;

  return (
    <div className="dt-container">
      {/* Overlay de progreso de importacion */}
      {importProgress && (
        <div className="import-progress-overlay">
          <div className="import-progress-card">
            {!progressDone ? (
              <>
                <div className="import-progress-spinner">
                  <svg viewBox="0 0 50 50" width="48" height="48">
                    <circle cx="25" cy="25" r="20" fill="none" stroke="rgba(59,130,246,0.2)" strokeWidth="4" />
                    <circle cx="25" cy="25" r="20" fill="none" stroke="#3b82f6" strokeWidth="4"
                      strokeDasharray={`${progressPct * 1.257} 125.7`}
                      strokeLinecap="round" transform="rotate(-90 25 25)"
                      style={{ transition: 'stroke-dasharray 0.3s ease' }} />
                  </svg>
                  <span className="import-progress-pct">{progressPct}%</span>
                </div>
                <div className="import-progress-text">
                  Importando registro {importProgress.current} de {importProgress.total}...
                </div>
                <div className="import-progress-bar-wrap">
                  <div className="import-progress-bar" style={{ width: `${progressPct}%` }} />
                </div>
                <div className="import-progress-counts">
                  <span className="import-count-ok">{importProgress.ok} creados</span>
                  {importProgress.fail > 0 && <span className="import-count-fail">{importProgress.fail} fallidos</span>}
                </div>
              </>
            ) : (
              <>
                <div className="import-progress-done-icon">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke={importProgress.ok > 0 ? '#22c55e' : '#ef4444'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {importProgress.ok > 0 ? (
                      <><circle cx="12" cy="12" r="10" /><path d="M8 12l2.5 2.5L16 9" /></>
                    ) : (
                      <><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></>
                    )}
                  </svg>
                </div>
                <div className="import-progress-text" style={{ fontWeight: 600 }}>
                  Importacion completada
                </div>
                <div className="import-progress-counts">
                  <span className="import-count-ok">{importProgress.ok} creados</span>
                  {importProgress.fail > 0 && <span className="import-count-fail">{importProgress.fail} fallidos</span>}
                </div>
                {importProgress.errors && importProgress.errors.length > 0 && (
                  <div className="import-errors-list">
                    <div className="import-errors-title">Detalle de errores:</div>
                    <div className="import-errors-scroll">
                      {importProgress.errors.map((err, idx) => (
                        <div key={idx} className="import-error-item">
                          <span className="import-error-bullet">x</span>
                          {typeof err === 'string' ? err : err.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                  {importProgress.errors && importProgress.errors.length > 0 && (
                    <button className="btn btn-secondary btn-sm" onClick={handleExportImportErrors}
                      title="Descarga un Excel con las filas que fallaron + el motivo, para corregirlas y reimportarlas">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Exportar errores
                    </button>
                  )}
                  <button className="btn btn-primary btn-sm" onClick={() => setImportProgress(null)}>
                    Cerrar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Toolbar — se oculta si no hay nada que mostrar */}
      <div className="dt-toolbar">
          <div className="dt-toolbar-left">
            {!hideSearch && (
              <div className="dt-filter">
                <svg className="dt-filter-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
                </svg>
                <input value={filter} onChange={e => handleFilter(e.target.value)} placeholder="Filtrar registros..." className="dt-filter-input" />
                {filter && <button className="dt-filter-clear" onClick={() => handleFilter('')}>&times;</button>}
              </div>
            )}
            <div className="dt-page-size">
              <span>Filas:</span>
              <select value={pageSize} onChange={e => handlePageSize(e.target.value)}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={999999}>Todos</option>
              </select>
            </div>
          </div>
          {!hideToolbarRight && (
            <div className="dt-toolbar-right">
              <button className="btn btn-secondary dt-toolbar-btn" onClick={handleDownloadTemplate} title="Descargar plantilla Excel">
                {/* Plantilla: hoja con flecha saliendo hacia abajo */}
                <svg viewBox="0 0 18 18" fill="none" width="20" height="20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="1" width="10" height="13" rx="1.5"/>
                  <path d="M5 5h6M5 8h6M5 11h4"/>
                  <path d="M14 11v5m0 0l-2-2m2 2 2-2"/>
                </svg>
                Plantilla
              </button>
              <button className="btn btn-secondary dt-toolbar-btn" onClick={handleImportClick} title="Importar desde Excel">
                {/* Importar: hoja con flecha entrando hacia arriba */}
                <svg viewBox="0 0 18 18" fill="none" width="20" height="20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="1" width="10" height="13" rx="1.5"/>
                  <path d="M5 5h6M5 8h6M5 11h4"/>
                  <path d="M14 16v-5m0 0-2 2m2-2 2 2"/>
                </svg>
                Importar
              </button>
              <button className="btn btn-secondary dt-toolbar-btn" onClick={handleExport} title="Exportar a Excel">
                {/* Exportar: hoja de cálculo con cuadrícula + flecha saliendo */}
                <svg viewBox="0 0 18 18" fill="none" width="20" height="20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="3" width="12" height="12" rx="1.5"/>
                  <path d="M4 3V1.5M7 3V1.5M10 3V1.5"/>
                  <path d="M1 7h12M1 11h12"/>
                  <path d="M4 7v8M7 7v8"/>
                  <path d="M14 1v6m0 0 2-2m-2 2-2-2"/>
                </svg>
                Exportar
              </button>
            </div>
          )}
        </div>
      <input type="file" ref={fileInputRef} accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={handleFileChange} />

      {/* Tabla */}
      <div className="data-table-container">
        {filtered.length === 0 ? (
          <div className="empty-state">{emptyMessage || 'No hay registros'}</div>
        ) : (
          (() => {
            const actionsWidth = (onEdit || onDelete || extraActions) ? (extraActions ? 180 : 130) : 0;
            const declaredSum = columns.reduce((acc, c) => acc + (typeof c.width === 'number' ? c.width : 0), 0);
            const tableMinWidth = declaredSum + actionsWidth;
            return (
          <table className="data-table" style={tableMinWidth > 0 ? { minWidth: tableMinWidth } : undefined}>
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col.key} style={col.width ? {width: col.width} : {}}>{col.label}</th>
                ))}
                {(onEdit || onDelete || extraActions) && <th style={{width: extraActions ? 180 : 130}}>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {paginated.map(row => (
                <tr key={row[idField]}>
                  {columns.map(col => (
                    <td key={col.key} style={col.style || {}}>
                      {col.render ? col.render(row) : (row[col.key] ?? '--')}
                    </td>
                  ))}
                  {(onEdit || onDelete || extraActions) && (
                    <td>
                      <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                        {onEdit && <button className="btn btn-secondary btn-sm" onClick={() => onEdit(row)}>Editar</button>}
                        {extraActions && extraActions(row).map((action, ai) => (
                          <button key={ai} className="btn btn-secondary btn-sm" onClick={() => action.onClick(row)}>{action.label}</button>
                        ))}
                        {onDelete && <button className="btn btn-danger btn-sm" onClick={() => onDelete(row)}>Eliminar</button>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
            );
          })()
        )}
      </div>

      {/* Footer: info + paginación */}
      {filtered.length > 0 && (
        <div className="dt-footer">
          <div className="dt-footer-left">
            <span className="dt-info">
              {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filtered.length)} de {filtered.length} registros
            </span>
          </div>
          <div className="dt-pagination">
            <button className="dt-page-btn" disabled={page === 0} onClick={() => setPage(0)}>&laquo;</button>
            <button className="dt-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>&lsaquo;</button>
            {getPageNumbers().map(p => (
              <button key={p} className={`dt-page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>
                {p + 1}
              </button>
            ))}
            <button className="dt-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>&rsaquo;</button>
            <button className="dt-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>&raquo;</button>
          </div>
        </div>
      )}

      {/* Modal de importacion preview */}
      {importData && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth: 700, overflow: 'hidden'}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Importar desde Excel</h2>
              <button className="modal-close" onClick={() => setImportData(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{fontSize: 13, color: 'rgba(148,163,184,0.8)', marginBottom: 16}}>
                Se encontraron <strong>{importData.length}</strong> registros.
                {importColumns ? (
                  <> Campos esperados: {importColumns.map(c => c.required ? <strong key={c.key}>{c.label} (*)</strong> : <span key={c.key}>{c.label}</span>).reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, ', ', curr], [])}.</>
                ) : (
                  <> Las columnas deben coincidir con: {columns.map(c => c.label).join(', ')}.</>
                )}
              </p>
              <div className="data-table-container" style={{maxHeight: 300, overflowY: 'auto'}}>
                <table className="data-table">
                  <thead>
                    <tr>{Object.keys(importData[0] || {}).map(k => <th key={k}>{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {importData.slice(0, 10).map((row, i) => (
                      <tr key={i}>{Object.values(row).map((v, j) => <td key={j}>{String(v)}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {importData.length > 10 && <p style={{fontSize: 12, color: '#64748b', marginTop: 8}}>Mostrando 10 de {importData.length} filas...</p>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setImportData(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleStartImport}>
                Importar {importData.length} registros
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
